import { useState, type JSX } from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import { queryClient } from "@impero-com/common/utils/query_client";
import { isAxiosError } from "axios";
import { Hash } from "history";
import { useNavigate, useLocation } from "react-router-dom";

import { PageState } from "api/generated/model";
import { PageKey } from "org/pageState/pageKey";
import { encodeKey, pageStateStorage } from "org/pageState/storage";

import { SHAREABLE_LINK_ID_QUERY_PARAM } from "../consts";
import { usePageStateMutation } from "../queries/usePageStateMutation";
import {
    PAGE_STATE_QUERY_KEY,
    usePageStateQuery,
} from "../queries/usePageStateQuery";

import { ShareableLinkPopup } from "./shareableLinkPopup";

// Params that are injected into renderContent() callback and can be used by the child components.
export interface RenderContentInnerArgs {
    // Data (filters, visible columns, etc)
    pageState: PageState;

    // Callback to set the new version of data
    onPageStateChange: (pageState: PageState) => void;

    // Is invoked when user clicks on "Share Link"
    onCreateShareableLink: () => void;
}

export interface PageStateManagerInnerProps {
    // A key that identifies the page. Used mostly for sessionStorage.
    pageKey: PageKey;

    // Renders the main content to display with extra values injected, so other components can use them.
    renderContent: (args: RenderContentInnerArgs) => JSX.Element;

    // Show when an error occurred
    renderError: (code: number | undefined) => JSX.Element;

    // Shown while loading (is relevant when shareableLinkId param is present in URL)
    renderLoading: () => JSX.Element;

    // Search params in URL that must be preserved for the shareable link URL.
    preservedSearchParams?: Array<string>;

    // A hook to modify pageState in memory once after it's loaded from localStorage.
    // Practically it's used for those pages, where filter can be defined through URL, but we don't want the filter from URL
    // overrides values in localStorage on init.
    onInitInMemoryPageStateUpdate: (pageState: PageState) => PageState;
}

export function PageStateManagerInner(
    props: PageStateManagerInnerProps
): JSX.Element {
    const {
        pageKey,
        renderContent,
        renderLoading,
        renderError,
        preservedSearchParams = [],
        onInitInMemoryPageStateUpdate,
    } = props;
    const shareableLinkId = useShareablLinkIdFromQuery();
    const removeShareableLinkIdFromQuery = useRemoveShareableLinkIdFromQuery();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [shareableLinkUrl, setShareableLinkUrl] = useState("");

    const {
        data: pageStateData,
        status: pageStateStatus,
        error: pageStateError,
    } = usePageStateQuery(pageKey, shareableLinkId, (pageState) =>
        onInitInMemoryPageStateUpdate(pageState)
    );
    const {
        mutateAsync: createShareableLink,
        status: createShareableLinkStatus,
    } = usePageStateMutation();

    switch (pageStateStatus) {
        case "pending":
            return renderLoading();
        case "error": {
            const hasAxiosError = isAxiosError(pageStateError);
            if (hasAxiosError) {
                const code = pageStateError.code;
                if (code === undefined) {
                    throw new Error("Expected http status code");
                }
                return renderError(Number(code));
            }
            throw new Error("Expected http status code");
        }
        case "success": {
            const onCreateShareableLink = (): void => {
                if (createShareableLinkStatus === "pending") {
                    return;
                }
                createShareableLink({
                    data: pageStateData,
                    preservedSearchParams,
                }).then((data) => {
                    setDialogOpen(true);
                    setShareableLinkUrl(data);
                });
            };

            const onPageStateChange = (pageState: PageState): void => {
                pageStateStorage.set(pageKey, pageState);
                removeShareableLinkIdFromQuery();
                queryClient.invalidateQueries({
                    queryKey: [PAGE_STATE_QUERY_KEY, encodeKey(pageKey)],
                });
            };
            const renderContentArgs: RenderContentInnerArgs = {
                pageState: pageStateData,
                onPageStateChange,
                onCreateShareableLink,
            };

            return (
                <>
                    <ShareableLinkPopup
                        open={dialogOpen}
                        linkUrl={shareableLinkUrl}
                        onClose={() => setDialogOpen(false)}
                    />
                    {renderContent(renderContentArgs)}
                </>
            );
        }
        default: {
            return assertNever(pageStateStatus);
        }
    }
}

function useSearchParamsAndHash(): { search: URLSearchParams; hash: Hash } {
    const { search, hash } = useLocation();
    return { search: new URLSearchParams(search), hash };
}

// Returns shareableLinkId URL param value.
function useShareablLinkIdFromQuery(): string | null {
    const { search } = useSearchParamsAndHash();
    return search.get(SHAREABLE_LINK_ID_QUERY_PARAM);
}

// Returns a function that removes shareableLinkId from URL params.
function useRemoveShareableLinkIdFromQuery(): () => void {
    const navigate = useNavigate();
    const { search, hash } = useSearchParamsAndHash();

    return () => {
        if (search.has(SHAREABLE_LINK_ID_QUERY_PARAM)) {
            search.delete(SHAREABLE_LINK_ID_QUERY_PARAM);
            navigate(
                {
                    search: search.toString(),
                    hash,
                },
                { replace: true }
            );
        }
    };
}
